import { Download, RefreshCw, Sparkles, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import InstallPWA from './InstallPWA';

// 🔒 Latches globais (por aba) para evitar múltiplos avisos e múltiplos listeners
let __pwaListenerAttached = false;
let __lastWaitingScriptURL: string | null = null;

export default function PWAUpdatePrompt() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const wantsReloadRef = useRef(false);
  const [reloaded, setReloaded] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onControllerChange = () => {
      // Recarrega apenas se o usuário pediu e houver rede
      if (wantsReloadRef.current && navigator.onLine) {
        wantsReloadRef.current = false;
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    // Listener para mensagem RELOAD_WINDOW do service worker
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'RELOAD_WINDOW' && !reloaded) {
        setReloaded(true);
        location.reload();
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);

    navigator.serviceWorker.ready.then((reg) => {
      const maybeShow = (sw: ServiceWorker | null) => {
        if (!sw) return;
        const url = (sw as any)?.scriptURL as string | undefined;
        const snoozeUntil = Number(sessionStorage.getItem('acor:pwa:snooze-until') || '0');
        const now = Date.now();
        // Se estamos em "soneca" ou já mostramos para este SW, não reabrir
        if (now < snoozeUntil) return;
        if (url && __lastWaitingScriptURL === url) return;
        setWaitingWorker(sw);
        __lastWaitingScriptURL = url ?? null;
        setShowUpdate(true);
      };

      // Mostra imediatamente se já existe waiting (ex.: página recarregada)
      if (reg.waiting) {
        maybeShow(reg.waiting);
      }

      // Anexa um único listener por aba
      if (!__pwaListenerAttached) {
        __pwaListenerAttached = true;
        reg.addEventListener('updatefound', () => {
          const sw = reg.installing || reg.waiting;
          if (!sw) return;
          sw.addEventListener('statechange', () => {
            // Quando o novo SW entra em "installed" com um controller ativo → há waiting
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
              maybeShow(reg.waiting || sw);
            }
          });
        });
      }
    });

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, [reloaded]);

  const handleUpdate = () => {
    if (!waitingWorker) return;

    setIsUpdating(true);
    // No botão "Atualizar agora":
    wantsReloadRef.current = true;
    setShowUpdate(false);
    try {
      (window as any).__pwaUserInitiatedReload = true;
      navigator.serviceWorker.ready.then((reg) => reg.waiting?.postMessage({ type: 'SKIP_WAITING' }));
    } catch {}
    // Listener adicional de controllerchange como fallback
    let once = false;
    const fallbackReload = () => {
      if (once) return;
      once = true;
      location.reload();
    };
    navigator.serviceWorker?.addEventListener('controllerchange', fallbackReload);
    // Fallback caso controllerchange não dispare
    setTimeout(() => {
      if (wantsReloadRef.current && navigator.onLine) {
        wantsReloadRef.current = false;
        window.location.reload();
      }
    }, 1500);
  };

  const handleDismiss = () => {
    setShowUpdate(false);
    // ⏰ Soneca de 1h mesmo que o componente remonte
    const until = Date.now() + 60 * 60 * 1000;
    sessionStorage.setItem('acor:pwa:snooze-until', String(until));
  };

  return (
    <>
      {/* Capturador headless do beforeinstallprompt montado globalmente */}
      <InstallPWA />
      {showUpdate && (
      <div className="fixed top-4 left-4 right-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 mx-auto max-w-sm">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-primary/10 dark:bg-blue-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-primary dark:text-blue-400" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-ageo-bold text-foreground dark:text-gray-100 text-base mb-1">
              Nova Versão Disponível
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Uma atualização do ACOR ERP está pronta para ser instalada com melhorias e correções.
            </p>
            
            <div className="flex gap-2">
              <button
                onClick={handleUpdate}
                disabled={isUpdating}
                className="flex items-center gap-2 bg-primary dark:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-ageo-regular disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 dark:hover:bg-blue-500 transition-colors"
              >
                {isUpdating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Atualizando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Atualizar Agora
                  </>
                )}
              </button>
              
              <button
                onClick={handleDismiss}
                disabled={isUpdating}
                className="px-4 py-2 rounded-lg text-sm font-ageo-regular text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Depois
              </button>
            </div>
          </div>
          
          <button
            onClick={handleDismiss}
            disabled={isUpdating}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
      )}
    </>
  );
}

// Hook para gerenciar o estado de atualização
export function usePWAUpdate() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        // ❌ Sem verificação automática; usuário decide quando atualizar

        // Listener para atualizações
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setUpdateAvailable(true);
            }
          });
        });

        return () => {};
      });
    }
  }, []);

  const triggerUpdate = async () => {
    setIsUpdating(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        // Aguardar um pouco antes de recarregar
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Erro ao forçar atualização:', error);
      setIsUpdating(false);
    }
  };

  return {
    isUpdating,
    updateAvailable,
    triggerUpdate
  };
}